import { useEffect, useState } from "react";
import ModalComponent from "..";
import { useGetAllSalesAgentsQuery } from "../../../redux/features/admin/sales/sales.api";
import { SingleSalesAgent } from "../../../redux/features/admin/sales/sales.types";
import { deepCopy } from "../../../utils/helpers";
import { ModalCompProps, QueryParams } from "../../../utils/types";
import { Button } from "../../buttons/button";
import { ProfileIcon } from "../../cards/profilePic";
import { Checkbox } from "../../inputs/checkbox";
import { SearchInput } from "../../inputs/searchInput";
import { TableComponent } from "../../table";

export const SelectSalesAgentModal = ({
  open,
  handleClose,
  onSuccess,
  maxSelection = 1,
  loading,
  proceedText = "Continue",
  initialSelected = [],
}: ModalCompProps & {
  onSuccess: (agent: SingleSalesAgent[]) => void;
  maxSelection?: number;
  loading?: boolean;
  proceedText?: string;
  initialSelected?: SingleSalesAgent[];
}) => {
  const [selected, setSelected] = useState<SingleSalesAgent[]>([]);
  const [query, setQuery] = useState<QueryParams>({
    page: 1,
    limit: 50,
    search: undefined,
  });

  const { data: agents, isFetching: fetchingAgents } =
    useGetAllSalesAgentsQuery(query, {
      refetchOnMountOrArgChange: true,
      skip: !open,
    });

  useEffect(() => {
    if (!open) {
      setSelected([]);
    }
  }, [open]);

  useEffect(() => {
    setSelected(initialSelected);
  }, [initialSelected]);

  return (
    <ModalComponent
      open={open}
      handleClose={() => {
        handleClose();
        setSelected([]);
      }}
      className=""
      dialogClassName=""
      closeOnOverlayClick
      showHeader
      title={"Assign to Sales Agent"}
      subtitle={undefined}
      showIcon={false}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <div className="sticky top-0 z-10 bg-white pb-4">
        <SearchInput
          searchFunction={(value) => {
            setQuery({
              ...query,
              search: value || undefined,
              page: 1,
            });
          }}
          placeholder="Search"
        />
      </div>

      <TableComponent
        headers={[]}
        className="!border-none"
        rowClassName="!px-0"
        isLoading={fetchingAgents}
        loadingRows={query.limit}
        isTableEmpty={[0, undefined].includes(agents?.data?.data?.length)}
        rows={
          agents?.data?.data?.map((agent) => {
            return {
              id: agent._id,
              items: [
                <Checkbox
                  key={`agent-${agent._id}`}
                  hideIconUntilSelect
                  checkStatus={selected.map((i) => i._id).includes(agent._id)}
                  disabled={
                    maxSelection !== 1 &&
                    selected.length === maxSelection &&
                    !selected.map((i) => i._id).includes(agent._id)
                  }
                  text={
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <ProfileIcon img="" dimension={32} className="" />
                      </div>

                      <p className="flex flex-1 flex-wrap items-center gap-0 text-sm text-grey-600">
                        <span className="font-bold text-grey-900">
                          {agent.firstName} {agent.lastName}
                        </span>
                        <span className={` `}>∙</span>
                        <span className={``}>{agent.email}</span>
                      </p>
                    </>
                  }
                  textClassName="flex-1 flex flex-wrap gap-2 ites-center"
                  textPosition="left"
                  className="w-full"
                  handleChange={(checked) => {
                    if (checked && maxSelection === 1) {
                      setSelected([agent]);
                    } else if (checked && maxSelection > 1) {
                      setSelected([...selected, agent]);
                    } else {
                      const selectedCopy = deepCopy(selected);
                      setSelected(
                        selectedCopy.filter((i) => i._id !== agent._id),
                      );
                    }
                  }}
                />,
              ],
            };
          }) ?? []
        }
        isPaginated={false}
        emptyTitle="No Sales Agents"
        emptySubTitle=""
        emptyImage="/assets/images/guest-glass.png"
      />

      <div className="sticky bottom-0 col-span-full flex w-full items-center justify-end gap-2 bg-white pt-6">
        <Button
          text="Cancel"
          type="button"
          onClick={() => {
            handleClose();
            setSelected([]);
          }}
          variation="plain-grey"
          className="max-w-fit"
        />
        <Button
          text={proceedText}
          type="button"
          onClick={() => {
            onSuccess(selected);
          }}
          className="max-w-fit"
          loading={loading}
          disabled={loading || selected.length === 0}
        />
      </div>
    </ModalComponent>
  );
};
